import type { ParsedUrlQuery } from 'querystring';

import type { FeatureFlags } from '@/models/Types';
import {
  isCloseknitReferral,
  readFirstQueryValue,
} from '@/utils/isCloseknitReferral';

/**
 * Legacy Pages Router redirect policy to CHAS DTI v2 basic-info.
 *
 * Unlike `/book`'s `buildV2DtiRedirect`, which allowlists query params, this
 * policy forwards every query key (first non-blank value) — legacy entry
 * points predate the allowlist and CHAS consumes arbitrary attribution params.
 */
type LegacyChasRedirectFlags = Pick<
  FeatureFlags,
  | 'redirectCulinaStartToChasDti'
  | 'enableCustomUrlRedirectionToChas'
  | 'excludeCloseknitFromChasDtiRedirect'
>;

const CHAS_BASIC_INFO_PRODUCTION_URL =
  'https://prod-chas.culinahealth.com/dti/steps/basic_info';
const CHAS_BASIC_INFO_STAGING_URL =
  'https://staging-chas.culinahealth-staging.com/dti/steps/basic_info';
const CHAS_BASIC_INFO_DEV_URL =
  'https://dev-chas.culinahealth-test.com/dti/steps/basic_info';
const CHAS_BASIC_INFO_URL_BY_START_HOSTNAME: Record<string, string> = {
  'staging-start.culinahealth-staging.com': CHAS_BASIC_INFO_STAGING_URL,
  'dev-start.culinahealth-test.com': CHAS_BASIC_INFO_DEV_URL,
  // Keep local development out of staging data while using the validated dev
  // CHAS intake without requiring a second local service or port convention.
  localhost: CHAS_BASIC_INFO_DEV_URL,
};

/**
 * Closeknit traffic stays in Culina Start. LaunchDarkly key
 * `exclude_closeknit_from_chas_dti_redirect` defaults on so a missing flag
 * keeps the exclusion.
 */
export const isExcludedCloseknitReferral = (
  query: ParsedUrlQuery,
  { excludeCloseknitFromChasDtiRedirect = true }: LegacyChasRedirectFlags,
): boolean => excludeCloseknitFromChasDtiRedirect && isCloseknitReferral(query);

const shouldRedirectToChas = (
  query: ParsedUrlQuery,
  flags: LegacyChasRedirectFlags,
): boolean => {
  if (!flags.redirectCulinaStartToChasDti) return false;
  if (isExcludedCloseknitReferral(query, flags)) return false;
  return !!flags.enableCustomUrlRedirectionToChas || !query.referring_practice;
};

const buildChasBasicInfoUrl = (
  query: ParsedUrlQuery,
  hostname: string,
): string => {
  const baseUrl =
    CHAS_BASIC_INFO_URL_BY_START_HOSTNAME[hostname] ??
    CHAS_BASIC_INFO_PRODUCTION_URL;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const firstValue = readFirstQueryValue(value);
    if (firstValue) params.append(key, firstValue);
  }
  const search = params.toString();
  return search ? `${baseUrl}?${search}` : baseUrl;
};

/** The CHAS DTI URL to hard-redirect to, or `null` to stay in Culina Start. */
export const resolveLegacyChasDtiRedirect = (
  query: ParsedUrlQuery,
  hostname: string,
  flags: LegacyChasRedirectFlags,
): string | null =>
  shouldRedirectToChas(query, flags)
    ? buildChasBasicInfoUrl(query, hostname)
    : null;
